"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
function ProjectSkeleton({
  "data-index": dataIndex
}: {
  "data-index": number;
}) {
  return <Card data-index={dataIndex} className="overflow-hidden border-border bg-background/50">
      <AspectRatio ratio={4 / 3}>
        <Skeleton className="h-full w-full" />
      </AspectRatio>
      <CardContent className="p-6">
        <Skeleton className="mb-4 h-6 w-2/3" />
        <Skeleton className="mb-2 h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
      </CardContent>
    </Card>;
}
function PortfolioGrid() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  const sortedPosts = React.useMemo(() => {
    if (!posts) return [];
    const priorityOrder = ["MIV GRUPO — Plataforma Industrial y Servicios Técnicos", "VENCHANDLERS NEXUS — Plataforma Marítima Internacional", "KIM & JULIO HOME RENEWALS — Local Service Website & Lead Generation Setup"];
    return [...posts].sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.title);
      const indexB = priorityOrder.indexOf(b.title);
      const weightA = indexA === -1 ? 999 : indexA;
      const weightB = indexB === -1 ? 999 : indexB;
      return weightA - weightB;
    });
  }, [posts]);
  if (loading) {
    return <div data-section-id="189441" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <ProjectSkeleton key={i} data-index={i} />)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[400px] items-center justify-center border border-dashed border-border p-8 text-center">
        <p className="text-muted-foreground">Error al cargar los casos de éxito. Por favor, inténtelo de nuevo.</p>
      </div>;
  }
  if (!sortedPosts.length) {
    return <div className="flex min-h-[400px] items-center justify-center border border-dashed border-border p-8 text-center">
        <p className="text-muted-foreground">No se encontraron proyectos en el portafolio actualmente.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {sortedPosts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border bg-background/60 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl">
            <div className="relative overflow-hidden">
              <AspectRatio ratio={4 / 3}>
                <img src={post.customFields?.project_image || post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1611697/img/generic_project_placeholder.png"} alt={post.title} className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" loading="lazy" />
              </AspectRatio>
              {post.customFields?.project_status && <div className="absolute right-4 top-4">
                  <Badge variant="secondary" className="border-none bg-secondary text-[10px] font-bold uppercase tracking-wider text-secondary-foreground backdrop-blur-md">
                    {post.customFields.project_status}
                  </Badge>
                </div>}
            </div>
            <CardHeader className="p-6 pb-4">
              <CardTitle className="font-sans text-xl uppercase tracking-tight text-foreground transition-colors group-hover:text-primary">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6 pt-0">
              <div className="flex flex-col gap-2 font-sans text-sm text-muted-foreground">
                {post.customFields?.location && <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-primary" />
                    <span>{post.customFields.location}</span>
                  </div>}
                {post.customFields?.completion_year && <div className="flex items-center gap-2">
                    <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                    <span>{post.customFields.completion_year}</span>
                  </div>}
              </div>
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {post.customFields?.short_description || post.excerpt?.replace(/<[^>]*>?/gm, "")}
              </p>
            </CardContent>
            <CardFooter className="px-6 pb-6 pt-0">
              <Button asChild variant="outline" className="w-full border-border font-bold uppercase tracking-widest transition-all duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-[0_0_20px_-5px_rgba(37,99,235,0.6)]">
                <Link to={post.link || "#"} className="flex w-full items-center justify-between">
                  <span>Ver Estrategia</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>)}
      </div>

      {(hasNext || hasPrev) && <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="gap-2 hover:bg-accent hover:text-accent-foreground">
                <ChevronLeft className="size-4" />
                Anterior
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="gap-2 hover:bg-accent hover:text-accent-foreground">
                Siguiente
                <ChevronRight className="size-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function Portfolio() {
  const wp_query_params = {
    per_page: 3
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="portfolio" className="relative overflow-hidden bg-background py-24">
      {}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-2xl">
          <Badge variant="outline" className="mb-4 border-primary text-primary font-bold uppercase tracking-widest">
            Implementaciones Exitosas
          </Badge>
          <h2 className="mb-6 text-4xl font-bold uppercase tracking-tighter text-foreground md:text-5xl">
            Transformaciones <span className="text-primary">Digitales Implementadas</span>
          </h2>
          <p className="text-lg text-muted-foreground italic border-l-2 border-secondary pl-4">
            Soluciones de automatización, sistemas AI y plataformas digitales implementadas en sectores técnicos, industriales y comerciales con resultados medibles.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 3
      }} className="projects-wrapper">
          <PortfolioGrid />
        </PostsProvider>
      </div>
    </section>;
}